/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
   dateParts,
   isOverdue,
   relativeDueLabel,
} from '../utils/dueDate';

// ---------------------------------------------------------------------------
// Linha de resultado em 2 níveis (design handoff §2.3).
//
// Substitui a linha de tabela de 10 colunas, cujo texto quebrava em 2-4 linhas.
// A informação foi reorganizada por importância: data à esquerda, identificação
// e contexto no centro (2 linhas, sem wrap), dinheiro à direita, status na
// ponta. O CNPJ da emissora saiu da linha (vive no filtro e no detalhe) — era a
// maior fonte de quebra.
//
// A linha inteira abre o detalhe; é um <div role="button"> com suporte a
// teclado, e não um <tr>, porque o layout deixou de ser tabular.
// ---------------------------------------------------------------------------

const STATUS_PILLS = {
   PENDING: {
      label: 'Pendente',
      color: '#7d5a00',
      bg: 'rgba(242,175,41,0.16)',
      border: 'rgba(242,175,41,0.45)',
   },
   OVERDUE: {
      label: 'Vencido',
      color: '#b3261e',
      bg: 'rgba(211,47,47,0.1)',
      border: 'rgba(211,47,47,0.28)',
   },
   RECEIVED: {
      label: 'Recebido',
      color: '#1b5e20',
      bg: 'rgba(46,125,50,0.12)',
      border: 'rgba(46,125,50,0.32)',
   },
   CANCELLED: {
      label: 'Cancelado',
      color: '#6b6b6b',
      bg: '#f2f2f2',
      border: 'rgba(0,0,0,0.08)',
   },
   REFUNDED: {
      label: 'Estornado',
      color: '#4a148c',
      bg: 'rgba(123,31,162,0.1)',
      border: 'rgba(123,31,162,0.3)',
   },
};

const COLLECTION_LABELS = {
   ATIVA: 'Ativa',
   PAGA: 'Paga',
   CANCELADA: 'Cancelada',
   RENEGOCIADA: 'Renegociada',
   REVENDEDOR: 'Revendedor',
   CONSULTH: 'Consulth',
   JURIDICO: 'Jurídico',
};

const Row = styled.div`
   display: flex;
   align-items: stretch;
   gap: 14px;
   padding: 12px 16px;
   border-bottom: 1px solid rgba(0, 0, 0, 0.06);
   background: #fff;
   cursor: pointer;
   transition: background 140ms ease;

   &:hover {
      background: #f5faff;
   }
   &:focus-visible {
      outline: none;
      box-shadow: inset 0 0 0 2px rgba(64, 152, 239, 0.5);
   }
   &:last-child {
      border-bottom: 0;
   }
`;

// Tom do bloco de data: atrasado (vermelho), encerrado/cancelado (cinza) ou
// normal (azul). Mapa em vez de ternário aninhado.
const DATE_TONES = {
   late: { bg: 'rgba(211,47,47,0.09)', color: '#b3261e' },
   off: { bg: '#f6f6f6', color: '#9a9a9a' },
   normal: { bg: '#f1f6fc', color: '#2f5c8a' },
};

const DateBlock = styled.div`
   flex: 0 0 54px;
   border-radius: 10px;
   padding: 6px 0;
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;

   background: ${({ $tone }) => (DATE_TONES[$tone] ?? DATE_TONES.normal).bg};
   color: ${({ $tone }) => (DATE_TONES[$tone] ?? DATE_TONES.normal).color};

   .day {
      font-size: 17px;
      font-weight: 800;
      font-variant-numeric: tabular-nums;
      line-height: 1.1;
   }
   .month {
      font-size: 10px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.08em;
   }
   .year {
      font-size: 10px;
      font-weight: 600;
      opacity: 0.7;
      font-variant-numeric: tabular-nums;
   }
`;

const Main = styled.div`
   flex: 1;
   min-width: 0;
   display: flex;
   flex-direction: column;
   justify-content: center;
   gap: 4px;
`;

const TitleLine = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   min-width: 0;

   .id {
      font-size: 11px;
      font-weight: 800;
      color: #666;
      background: #f2f2f2;
      border-radius: 6px;
      padding: 2px 6px;
      font-variant-numeric: tabular-nums;
      flex: 0 0 auto;
   }

   .customer {
      font-size: 14px;
      font-weight: 700;
      color: #111;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 340px;
   }

   .late {
      flex: 0 0 auto;
      font-size: 11px;
      font-weight: 800;
      color: #b3261e;
      background: rgba(211, 47, 47, 0.1);
      border: 1px solid rgba(211, 47, 47, 0.28);
      border-radius: 999px;
      padding: 2px 8px;
   }
`;

const MetaLine = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   flex-wrap: wrap;
   font-size: 12px;
   font-weight: 600;
   color: #7a7a7a;
   min-width: 0;

   .strong {
      color: #555;
      font-weight: 700;
   }

   .sep {
      opacity: 0.4;
   }

   /* Abaixo de 1000px a origem sai, em vez de deixar a linha 2 quebrar. */
   @media (max-width: 1000px) {
      .origin {
         display: none;
      }
      .origin-sep {
         display: none;
      }
   }
`;

// Linha 3 — empresa emissora numa badge própria. Saiu da linha de meta (onde
// disputava espaço com pedido/NF/origem e era a maior fonte de wrap) e ganhou
// uma faixa só dela: com multi-CNPJ, "de qual empresa é este título" é leitura
// frequente e merece um alvo visual estável.
const IssuerBadge = styled.span`
   align-self: flex-start;
   max-width: 100%;
   display: inline-flex;
   align-items: center;
   gap: 6px;
   font-size: 11px;
   font-weight: 800;
   color: #0b3a63;
   background: rgba(64, 152, 239, 0.12);
   border: 1px solid rgba(64, 152, 239, 0.25);
   border-radius: 6px;
   padding: 2px 8px;

   .name {
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
   }

   .icon {
      flex: 0 0 auto;
      opacity: 0.7;
      font-weight: 700;
   }
`;

const Amount = styled.div`
   display: flex;
   flex-direction: column;
   align-items: flex-end;
   justify-content: center;
   gap: 4px;
   flex: 0 0 auto;

   .value {
      font-size: 15px;
      font-weight: 800;
      color: #111;
      font-variant-numeric: tabular-nums;
      line-height: 1.2;
   }

   .relative {
      font-size: 11px;
      font-weight: 600;
      color: #9a9a9a;
      white-space: nowrap;
   }

   /* Recebido (handoff 0105) — mesmo tom discreto do .relative; a linha só
      existe quando amount_received foi informado (null = não renderiza). */
   .received {
      font-size: 11px;
      font-weight: 600;
      color: #9a9a9a;
      white-space: nowrap;
      font-variant-numeric: tabular-nums;
   }
`;

const InstallmentPill = styled.span`
   font-size: 11px;
   font-weight: 700;
   border-radius: 999px;
   padding: 2px 8px;
   white-space: nowrap;

   color: ${({ $multi }) => ($multi ? '#0b3a63' : '#8a8a8a')};
   background: ${({ $multi }) =>
      $multi ? 'rgba(64,152,239,0.12)' : '#f6f6f6'};
   border: 1px solid
      ${({ $multi }) => ($multi ? 'rgba(64,152,239,0.25)' : 'rgba(0,0,0,0.06)')};
`;

const StatusCol = styled.div`
   flex: 0 0 112px;
   display: flex;
   flex-direction: column;
   align-items: flex-end;
   justify-content: center;
   gap: 4px;

   .collection {
      font-size: 11px;
      font-weight: 700;
      color: #8a8a8a;
   }
`;

const StatusPill = styled.span`
   font-size: 11px;
   font-weight: 800;
   border-radius: 999px;
   padding: 3px 10px;
   white-space: nowrap;
   color: ${({ $color }) => $color};
   background: ${({ $bg }) => $bg};
   border: 1px solid ${({ $border }) => $border};
`;

const Chevron = styled.span`
   flex: 0 0 auto;
   display: flex;
   align-items: center;
   font-size: 18px;
   color: #c4c4c4;
`;

export default function ChargeRow({ charge, onOpen, formatCurrency }) {
   const { day, month, year } = dateParts(charge.due_date);
   const late = isOverdue(charge.due_date, charge.status);
   const isClosed =
      charge.status === 'CANCELLED' || charge.status === 'REFUNDED';

   let tone = 'normal';
   if (late) tone = 'late';
   else if (isClosed) tone = 'off';
   const pill = STATUS_PILLS[charge.status] ?? {
      label: charge.status,
      color: '#6b6b6b',
      bg: '#f2f2f2',
      border: 'rgba(0,0,0,0.08)',
   };

   const isMultiInstallment = charge.installment_count > 1;
   // `bankAccount` é nullable (cobrança órfã criada por caminho não previsto):
   // a badge continua aparecendo, dizendo o que houve, em vez de sumir e deixar
   // a linha com altura diferente das vizinhas.
   const issuer = charge.bankAccount?.account_name ?? 'Sem empresa vinculada';
   const issuerTitle = charge.bankAccount?.account_owner_name || undefined;
   const origin = charge.billingOrigin?.name ?? 'Sem origem';

   const handleKeyDown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
         event.preventDefault();
         onOpen();
      }
   };

   return (
      <Row
         role="button"
         tabIndex={0}
         onClick={onOpen}
         onKeyDown={handleKeyDown}
         title="Abrir detalhe do título"
      >
         <DateBlock $tone={tone}>
            <span className="day">{day}</span>
            <span className="month">{month}</span>
            <span className="year">{year}</span>
         </DateBlock>

         <Main>
            <TitleLine>
               <span className="id">#{charge.id}</span>
               <span className="customer">{charge.customer_name}</span>
               {late && <span className="late">Atrasado</span>}
            </TitleLine>
            <MetaLine>
               <span>
                  Pedido{' '}
                  <span className="strong">{charge.order_number ?? '—'}</span>
               </span>
               {/* NF-e só aparece quando existe: boleto avulso sem nota é
                   comum, e "NF —" seria ruído em toda linha. */}
               {charge.nfe_number && (
                  <>
                     <span className="sep">·</span>
                     <span>
                        NF-e{' '}
                        <span className="strong">{charge.nfe_number}</span>
                     </span>
                  </>
               )}
               <span className="sep origin-sep">·</span>
               <span className="origin">{origin}</span>
            </MetaLine>

            <IssuerBadge title={issuerTitle}>
               <span className="icon" aria-hidden="true">
                  ⌂
               </span>
               <span className="name">{issuer}</span>
            </IssuerBadge>
         </Main>

         <Amount>
            <span className="value">
               {formatCurrency(charge.installment_value)}
            </span>
            {/* != null cobre null E undefined (backend sem o 0105). O sufixo
                "+R$ X" é o acréscimo; o rótulo por extenso vai no title —
                sempre "juros e multa", nunca só "juros" (ASAAS não separa). */}
            {charge.amount_received != null && (
               <span
                  className="received"
                  title={
                     Number(charge.surcharge_amount) > 0
                        ? `Inclui juros e multa de ${formatCurrency(
                             charge.surcharge_amount
                          )}`
                        : undefined
                  }
               >
                  Recebido {formatCurrency(charge.amount_received)}
                  {Number(charge.surcharge_amount) > 0 &&
                     ` · +${formatCurrency(charge.surcharge_amount)}`}
               </span>
            )}
            <InstallmentPill $multi={isMultiInstallment}>
               {isMultiInstallment
                  ? `Parcela ${charge.installment_number} de ${charge.installment_count}`
                  : 'Parcela única'}
            </InstallmentPill>
            <span className="relative">
               {relativeDueLabel(charge.due_date, charge.status)}
            </span>
         </Amount>

         <StatusCol>
            <StatusPill
               $color={pill.color}
               $bg={pill.bg}
               $border={pill.border}
            >
               {pill.label}
            </StatusPill>
            <span className="collection">
               {COLLECTION_LABELS[charge.collection_status] ??
                  charge.collection_status ??
                  ''}
            </span>
         </StatusCol>

         <Chevron aria-hidden="true">›</Chevron>
      </Row>
   );
}

ChargeRow.propTypes = {
   charge: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      customer_name: PropTypes.string,
      order_number: PropTypes.string,
      nfe_number: PropTypes.string,
      due_date: PropTypes.string,
      installment_value: PropTypes.oneOfType([
         PropTypes.string,
         PropTypes.number,
      ]),
      amount_received: PropTypes.oneOfType([
         PropTypes.string,
         PropTypes.number,
      ]),
      surcharge_amount: PropTypes.oneOfType([
         PropTypes.string,
         PropTypes.number,
      ]),
      installment_number: PropTypes.number,
      installment_count: PropTypes.number,
      status: PropTypes.string,
      collection_status: PropTypes.string,
      bankAccount: PropTypes.shape({
         account_name: PropTypes.string,
         account_owner_name: PropTypes.string,
      }),
      billingOrigin: PropTypes.shape({ name: PropTypes.string }),
   }).isRequired,
   onOpen: PropTypes.func.isRequired,
   formatCurrency: PropTypes.func.isRequired,
};
